<template>
  <DataTable
    :key="offerProducts?.length"
    :value="offerProducts"
    :reorderable-columns="!isSorting && !isOnePOOffer && isEditable"
    :loading="isSorting"
    table-style="margin-top: 1rem"
    :pt="{
      footer: {
        style: { padding: 0, border: !canAppendOffers && 'none' },
      },
    }"
    @row-reorder="onRowReOrder"
  >
    <Column
      v-if="!isOnePOOffer && isEditable"
      row-reorder
      style="width: 5%"
      :reorderable-column="false"
      :pt="{
        headerCell: {
          class: $style.theadStyle,
        },
        root: {
          style: {
            borderLeft: '1px solid var(--vd-color-text-light-9) !important',
          },
        },
      }"
    />
    <Column
      v-if="isOnePOOffer"
      :reorderable-column="false"
      style="width: 10%"
      :pt="{
        headerCell: {
          class: $style.theadStyle,
        },
      }"
    >
      <template #body="{ data }">
        <VdIcon
          v-if="isEditable"
          store="fas"
          name="trash"
          :class="$style.deleteIcon"
          @click="() => deleteReward(data.id)"
        />
      </template>
    </Column>
    <Column
      v-if="!isOnePOOffer"
      :reorderable-column="false"
      header="Title"
      style="width: 15%"
      :pt="{
        headerCell: {
          class: $style.theadStyle,
        },
      }"
    >
      <template #body="{ data }">
        <div :class="$style.rewardTitle">
          <div :style="{ flex: 1, width: '90%' }" @click="(event) => toggleTitlePanel(event, data.id)">
            <span v-if="data.title">
              {{ data.title }}
            </span>
            <VdIcon
              v-if="isEditable"
              style="float: right; padding-top: 3px"
              store="fa"
              name="pen-to-square"
              size="small"
              color="var(--vd-color-text-light-2)"
            />
          </div>
          <VdIcon
            v-if="isEditable"
            store="fas"
            name="trash"
            :class="$style.deleteIcon"
            @click="() => deleteReward(data.id)"
          />
        </div>
        <Popover ref="titlePanel">
          <div :class="$style.titleForm">
            <InputText v-model="rewardTitle" placeholder="Title" :class="$style.titleInput" />
            <Button label="Save" :disabled="rewardTitle.length === 0" @click="handleTitleChange" />
          </div>
        </Popover>
      </template>
    </Column>
    <Column
      :reorderable-column="false"
      header="Rewards"
      :style="{ width: '30%' }"
      :pt="{
        headerCell: {
          class: $style.theadStyle,
        },
        root: {
          style: {
            borderRight: '2px solid var(--vd-color-text-light-9) !important',
          },
        },
      }"
    >
      <template #body="{ data }">
        <div
          :class="$style.rewardsWrapper"
          :style="{ cursor: isEditable ? 'pointer' : 'initial' }"
          @click="(e) => toggleRewardsPanel(e, data.id)"
        >
          <div :class="$style.rewardItemsWrapper">
            <div v-for="reward in data.items" :key="reward.id" :class="$style.rewardItem">
              <PriceItemWithQuantity :store-item="reward.item" :price-amount="reward.quantity" />
            </div>
          </div>
          <VdIcon v-if="isEditable" store="fa" name="chevron-down" size="small"></VdIcon>
        </div>

        <Popover ref="rewardsPanel">
          <ItemsBundlesList
            :reward-items="formatRewardItems(rewardItems)"
            :show-confirmation="false"
            exclude-non-consumables
            @save="(items) => handleItemsSave(items)"
          />
        </Popover>
      </template>
    </Column>
    <Column
      :reorderable-column="false"
      :style="{ width: '30%' }"
      :pt="{
        headerCell: {
          class: $style.theadStyle,
        },
      }"
    >
      <template #header>
        <div :class="$style.priceHeader">
          <span>Price iOS</span>
          <span>Price Android</span>
        </div>
      </template>
      <template #body="{ data }">
        <PriceTypeDropdown
          :is-editable="isEditable"
          :table-data="data"
          :selected-price-type="data.priceType"
          :non-consumable-ids="getNonConsumableIds(data)"
          @update-iap-id="(event: IapChangeEvent) => handleIapIdChange(event, data)"
          @update-rv-amount="(event) => handleRvAmountChange(event, data)"
          @update-in-game-price="(event) => handleInGamePriceChange(event, data)"
          @update-price-type="(event) => handlePriceTypeChange(event)"
          @update-free-reward="() => handleFreeRewardChange(data)"
        />
      </template>
    </Column>
    <Column
      :reorderable-column="false"
      header="Tags"
      :style="{ width: '15%' }"
      :pt="{
        headerCell: {
          class: $style.theadStyle,
        },
      }"
    >
      <template #body="slotProps">
        <TagsManager
          :model-value="slotProps.data.tags"
          :item-id="slotProps.data.id"
          :game-id="appId"
          :version-id="versionId"
          :max-visible-tags="2"
          :disabled="!isEditable"
          :type="'SHOP_ITEM'"
          @update:model-value="(tags) => handleTagUpdate(tags, slotProps.data.id)"
        />
      </template>
    </Column>
    <template #footer>
      <OfferProductForm
        v-if="canAppendOffers && isEditable && !isCreating"
        :offer-type="offerType"
        :version-id="versionId"
        :app-id="appId"
        :current-products-count="offerProducts?.length || 0"
        @add-reward="handleAddReward"
      />
    </template>
  </DataTable>
</template>

<script setup lang="ts">
import {
  OfferProduct,
  OfferProductInput,
  OfferProductItem,
  OfferType,
  SellableProductPriceType,
  useCreateOfferProductMutation,
  useDeleteOfferProductMutation,
  useSortOfferProductsMutation,
  useUpdateOfferProductMutation,
} from "@/graphql";
import ItemsBundlesList from "@/modules/ItemsBundles";
import { FormattedItem } from "@/modules/Rewards/types";
import Button from "primevue/button";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import Popover from "primevue/popover";
import { useConfirm } from "primevue/useconfirm";
import { PropType, computed, ref } from "vue";
import { useRoute } from "vue-router";

import PriceItemWithQuantity from "../components/PriceItemWithQuantity.vue";
import PriceTypeDropdown from "../components/PriceTypeDropdown/PriceTypeDropdown.vue";
import { mapOfferProductInput } from "../utils";
import OfferProductForm from "./OfferProductForm.vue";

const emit = defineEmits(["refetch-offer-products"]);

const props = defineProps({
  offerId: {
    type: String,
    required: true,
  },
  isEditable: {
    type: Boolean,
    required: true,
  },
  type: {
    type: String,
    required: true,
  },
  offerProducts: {
    type: Array as PropType<OfferProduct[]>,
  },
  offerType: {
    type: String,
    required: true,
  },
});

const confirm = useConfirm();
const route = useRoute();
const appId = ref<string>(route.params.gameId as string);
const versionId = ref<string>(route.params.versionId as string);

const apiRequestsPayload = {
  appId: appId.value,
  version: versionId.value,
  offerId: props.offerId,
};

const { mutate: createOfferProduct, loading: isCreating } = useCreateOfferProductMutation({});
const { mutate: deleteOfferProduct } = useDeleteOfferProductMutation({});
const { mutate: updateOfferProduct } = useUpdateOfferProductMutation({});
const { mutate: sortOfferProducts, loading: isSorting } = useSortOfferProductsMutation({});

const isOnePOOffer = computed(() => props.offerType === OfferType.OnePo);

const canAppendOffers = computed(
  () =>
    (props.offerProducts &&
      ((props.offerProducts?.length < 1 && props.offerType === OfferType.OnePo) ||
        (props.offerProducts?.length < 2 && props.offerType === OfferType.TwoPo) ||
        (props.offerProducts?.length < 3 && props.offerType === OfferType.ThreePo))) ||
    props.offerType === OfferType.Npo
);

const rewardsPanel = ref<InstanceType<typeof Popover> | null>(null);
const titlePanel = ref<InstanceType<typeof Popover> | null>(null);
const selectedPriceType = ref(SellableProductPriceType.Iap);
const rewardTitle = ref("");
const selectedReward = ref("");
const rewardItems = ref<OfferProductItem[]>([]);

const getNonConsumableIds = (product: OfferProduct) => {
  const nonConsumableIds = product.items
    ?.filter((item) => item.item?.type === "NON_CONSUMABLE")
    .map((item) => item.itemId);

  return nonConsumableIds || [];
};

const toggleRewardsPanel = (event: MouseEvent, offerProductId: string) => {
  const offerSpinToEdit = props.offerProducts?.find((offer) => offer && offer.id === offerProductId);

  if (offerSpinToEdit) {
    rewardItems.value = offerSpinToEdit.items;
  }

  selectedReward.value = offerProductId;

  if (!props.isEditable) {
    return;
  }

  rewardsPanel.value?.toggle(event);
};

const toggleTitlePanel = (event: MouseEvent, offerProductId: string) => {
  if (!props.isEditable) {
    return;
  }

  selectedReward.value = offerProductId;
  const offerProductToEdit = props.offerProducts?.find((offer) => offer && offer.id === offerProductId);

  if (offerProductToEdit) {
    rewardTitle.value = offerProductToEdit.title;
  }

  titlePanel.value?.toggle(event);
};

const formatRewardItems = (items: OfferProductItem[]) =>
  items?.map((item) => ({
    selectedItem: {
      id: item.itemId || "",
      name: item.item?.name || "",
      type: item.item?.type || "",
      sprite: {
        imageUrl: item.item?.sprite?.imageUrl || "",
        name: item.item?.sprite?.name || "",
        id: item.item?.sprite?.id || "",
      },
    },
    quantity: item.quantity,
  }));

const handleAddReward = async (payload: OfferProductInput) => {
  try {
    await createOfferProduct({
      ...apiRequestsPayload,
      input: payload,
    });

    emit("refetch-offer-products");
  } catch (error) {
    console.log(error, "error");
  }
};

const deleteReward = async (offerProductId: string) => {
  confirm.require({
    message: "Are you sure want to delete this reward?",
    header: "Confirmation",
    rejectClass: "p-button-secondary p-button-outlined",
    acceptClass: "p-button-danger",
    acceptIcon: "pi pi-trash",
    rejectLabel: "Cancel",
    acceptLabel: "Delete",
    accept: async () => {
      try {
        await deleteOfferProduct({
          ...apiRequestsPayload,
          offerProductId,
        });

        emit("refetch-offer-products");
      } catch (error) {
        console.log(error, "error");
      }
    },
  });
};

const updateReward = async (offerProductId: string, input: OfferProductInput) => {
  try {
    await updateOfferProduct({
      ...apiRequestsPayload,
      offerProductId,
      input,
    });

    emit("refetch-offer-products");
  } catch (error) {
    console.log(error, "error");
  }
};

const handleTitleChange = async () => {
  const originalOffer = props.offerProducts?.find((offer) => offer && offer.id === selectedReward.value);
  if (!originalOffer) {
    return;
  }

  const payload = mapOfferProductInput(originalOffer as OfferProduct);
  payload.title = rewardTitle.value;

  await updateReward(selectedReward.value, payload);

  titlePanel.value?.hide();
  rewardTitle.value = "";
};

interface IapChangeEvent {
  ios: string;
  android: string;
}

const handleIapIdChange = async (event: IapChangeEvent, product: OfferProduct) => {
  const payload = mapOfferProductInput(product);

  payload.iosPrice.IAPId = event.ios;
  payload.androidPrice.IAPId = event.android;
  payload.androidPrice.inGamePrice = null;
  payload.androidPrice.rvQuantity = null;
  payload.iosPrice.inGamePrice = null;
  payload.iosPrice.rvQuantity = null;
  payload.priceType = SellableProductPriceType.Iap;

  await updateReward(product.id, payload);
};

interface RvAmountChangeEvent {
  ios: number;
  android: number;
}

const handleRvAmountChange = async (event: RvAmountChangeEvent, product: OfferProduct) => {
  const payload = mapOfferProductInput(product);

  payload.iosPrice.rvQuantity = event.ios;
  payload.androidPrice.rvQuantity = event.android;
  payload.priceType = SellableProductPriceType.Rv;
  payload.androidPrice.inGamePrice = null;
  payload.androidPrice.IAPId = null;
  payload.iosPrice.inGamePrice = null;
  payload.iosPrice.IAPId = null;

  await updateReward(product.id, payload);
};

interface Price {
  quantity: number;
  itemId: string;
}

interface PlatformPrices {
  ios: Price;
  android: Price;
}

const handleInGamePriceChange = async (price: PlatformPrices, product: OfferProduct) => {
  const payload = mapOfferProductInput(product);

  payload.iosPrice.inGamePrice = price.ios;
  payload.androidPrice.inGamePrice = price.android;
  payload.priceType = SellableProductPriceType.InGameCurrency;
  payload.androidPrice.rvQuantity = null;
  payload.androidPrice.IAPId = null;
  payload.iosPrice.rvQuantity = null;
  payload.iosPrice.IAPId = null;

  await updateReward(product.id, payload);
};

const handleFreeRewardChange = async (product: OfferProduct) => {
  const payload = mapOfferProductInput(product);

  payload.priceType = SellableProductPriceType.Free;
  payload.androidPrice.rvQuantity = null;
  payload.androidPrice.IAPId = null;
  payload.androidPrice.inGamePrice = null;
  payload.iosPrice.rvQuantity = null;
  payload.iosPrice.IAPId = null;
  payload.iosPrice.inGamePrice = null;

  await updateReward(product.id, payload);
};

const handlePriceTypeChange = async (value: SellableProductPriceType) => {
  selectedPriceType.value = value;
};

const handleItemsSave = async (items: FormattedItem[]) => {
  const originalOffer = props.offerProducts?.find((offer) => offer && offer.id === selectedReward.value);

  if (!originalOffer) {
    return;
  }

  const payload = mapOfferProductInput(originalOffer as OfferProduct);

  payload.items = items.map((item) => ({
    itemId: item.selectedItem?.id || "",
    quantity: item.quantity,
  }));

  await updateReward(selectedReward.value, payload);

  rewardsPanel.value?.hide();
};

async function handleTagUpdate(tags: { id: string; name: string }[], offerProductId: string) {
  const originalOffer = props.offerProducts?.find((offer) => offer && offer.id === offerProductId);

  if (!originalOffer) {
    return;
  }

  const payload = mapOfferProductInput(originalOffer as OfferProduct);
  payload.tags = tags.map((tag) => ({
    id: tag.id,
    name: tag.name,
  }));

  await updateReward(offerProductId, payload);
}

async function onRowReOrder(event: { dragIndex: number; dropIndex: number; value: OfferProduct[] }) {
  // change the ranking of the element in the array based on the dragIndex
  const sortingPayload = event.value.map((offerProduct, index) => ({
    id: offerProduct.id,
    rank: index + 1,
  }));

  await sortOfferProducts({
    input: sortingPayload,
    offerId: props.offerId,
    appId: appId.value,
    version: versionId.value,
  });

  emit("refetch-offer-products");
}
</script>

<style module lang="scss">
.theadStyle {
  background-color: var(--vd-color-text-light-9) !important;
  border: 2px solid var(--vd-color-text-light-9) !important;
}

.rewardsWrapper {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .rewardItemsWrapper {
    width: 100%;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  svg {
    color: var(--vd-color-text-light-2);
  }
}

.deleteIcon {
  cursor: pointer;
  font-size: 12px;
  color: var(--vd-color-text-light-2);
  margin-left: 1rem;
}

.rewardTitle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  min-width: 100px;
}

.titleForm {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rewardItem {
  display: flex;
  align-items: center;
}

.priceHeader {
  display: flex;
  align-items: center;
  width: 100%;

  span {
    width: 50%;
  }
}
</style>

<style scoped>
:deep(.p-datatable-table-container) {
  overflow: initial !important;
}
</style>
